// Reusable modal + form helpers for admin CRUD
import React, { useRef, useState } from 'react';
import { X, Upload, ImagePlus, Trash2, Lock, AlertTriangle } from 'lucide-react';
import { API } from '../../api';

export function Modal({ open, title, onClose, children, maxWidth = 'max-w-2xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-white shadow-2xl w-full ${maxWidth} max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl sm:rounded-xl`} onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-bold text-base sm:text-lg truncate pr-3">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded flex-shrink-0" aria-label="Chiudi"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

export function Input(props) {
  return <input {...props} className={`w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[color:var(--yup-primary)] focus:ring-2 focus:ring-[color:var(--yup-primary)]/20 ${props.className || ''}`} />;
}
export function Textarea(props) {
  return <textarea {...props} className={`w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[color:var(--yup-primary)] focus:ring-2 focus:ring-[color:var(--yup-primary)]/20 ${props.className || ''}`} />;
}
export function Select(props) {
  return <select {...props} className={`w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[color:var(--yup-primary)] focus:ring-2 focus:ring-[color:var(--yup-primary)]/20 bg-white ${props.className || ''}`} />;
}


/**
 * Slug input with safety lock. When the record already exists (`isEditing`
 * true) the slug is read-only by default and the admin must explicitly click
 * "Sblocca modifica slug" (with a confirm dialog) before typing. Any character
 * typed while unlocked is normalized to lowercase and spaces are replaced with
 * dashes so the resulting URL stays clean.
 *
 * Props:
 *   value: current slug string
 *   onChange: (newSlug) => void
 *   isEditing: whether the parent form is editing an existing record
 *   entityLabel: e.g. "città" / "zona" — used inside the warning copy
 *   placeholder: input placeholder
 *   testId: prefix for data-testid attrs (slug input + unlock button)
 */
export function SlugField({ value, onChange, isEditing, entityLabel = 'record', placeholder, testId = 'slug' }) {
  const [unlocked, setUnlocked] = useState(false);
  const locked = isEditing && !unlocked;
  const normalize = (raw) => (raw || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
  const handleUnlock = () => {
    const ok = window.confirm(
      `Sei sicuro di voler modificare lo slug di questa ${entityLabel}?\n\n` +
      `Questo cambierà l'URL pubblico della pagina. Eventuali link già condivisi con lo slug attuale smetteranno di funzionare.`
    );
    if (ok) setUnlocked(true);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
        <label className="block text-sm font-medium text-gray-700">Slug (URL)</label>
        {isEditing && !unlocked && (
          <button
            type="button"
            onClick={handleUnlock}
            data-testid={`${testId}-unlock-btn`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 text-[11px] font-semibold"
          >
            <Lock size={11}/> Sblocca modifica slug
          </button>
        )}
      </div>
      <Input
        required
        disabled={locked}
        value={value || ''}
        onChange={(e) => onChange(normalize(e.target.value))}
        placeholder={placeholder}
        data-testid={testId}
        className={locked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
      />
      {isEditing && unlocked && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`${testId}-warning`}>
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5"/>
          <div>
            <strong>Attenzione</strong>: modificando lo slug cambi l'URL pubblico della pagina.
            Eventuali link già condivisi (social, QR code, motori di ricerca) con lo slug precedente smetteranno di funzionare.
            Lo slug viene sempre convertito in minuscolo con trattini al posto degli spazi.
          </div>
        </div>
      )}
      {!isEditing && (
        <p className="text-xs text-gray-500 mt-1">Solo minuscole, numeri e trattini (es. "lago-di-garda").</p>
      )}
    </div>
  );
}


export function I18nInputs({ value, onChange, kind = 'input', fallbackText = '' }) {
  const langs = [
    { code: 'it', label: 'Italiano' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'zh', label: '中文' },
    { code: 'ar', label: 'العربية', rtl: true },
  ];
  const Comp = kind === 'textarea' ? Textarea : Input;
  const [busy, setBusy] = useState(null);

  const translate = async (target) => {
    // Prefer the IT entry inside the group; fall back to the caller-provided
    // Italian text (e.g. Zone.name / City.name lives outside the i18n group
    // but represents the same Italian source).
    const src = ((value?.it || '').trim() || (fallbackText || '').trim());
    if (!src) { alert('Compila prima il campo in italiano (IT).'); return; }
    setBusy(target);
    try {
      const { adminApi } = await import('../../api');
      const { translation } = await adminApi.translateActivity(src, 'it', target);
      onChange({ ...value, [target]: translation });
    } catch (e) {
      alert('Errore traduzione: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {langs.map(l => (
        <div key={l.code}>
          <div className="text-xs font-semibold text-gray-500 mb-1 flex items-center justify-between">
            <span className="uppercase">{l.code} · {l.label}</span>
            {l.code !== 'it' && (
              <button
                type="button"
                onClick={() => translate(l.code)}
                disabled={busy === l.code}
                data-testid={`translate-${l.code}-btn`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed normal-case tracking-normal"
                title="Traduci da IT con AI (Emergent LLM)"
              >
                {busy === l.code ? '...' : '✨ AI'}
              </button>
            )}
          </div>
          <Comp
            dir={l.rtl ? 'rtl' : 'ltr'}
            value={value?.[l.code] || ''}
            onChange={e => onChange({ ...value, [l.code]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}

export function HoursEditor({ value, onChange }) {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const labels = { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Gio', fri: 'Ven', sat: 'Sab', sun: 'Dom' };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {days.map(d => (
        <div key={d} className="flex items-center gap-2">
          <span className="w-10 text-sm font-semibold text-gray-600">{labels[d]}</span>
          <Input value={value?.[d] || ''} onChange={e => onChange({ ...(value || {}), [d]: e.target.value })} placeholder="09:00-18:00 o Chiuso" />
        </div>
      ))}
    </div>
  );
}

// ========== IMAGE UPLOAD (drag & drop) ==========
export function ImageUpload({ value, onChange, hint }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);

  const previewUrl = value && !value.startsWith('http') && !value.startsWith('data:') ? `${API.replace('/api','')}${value}` : value;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Solo immagini'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Massimo 10 MB'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('yup_admin_token');
      const res = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onChange(data.url);
    } catch (e) {
      setError(e.message || 'Errore upload');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  return (
    <div>
      <div
        className={`dropzone ${dragOver ? 'drag-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {value ? (
          <div className="dropzone-preview">
            <img src={previewUrl} alt="preview" />
            <div className="flex items-center justify-center gap-3 mt-2">
              <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} className="text-sm text-[color:var(--yup-primary)] flex items-center gap-1"><ImagePlus size={14}/> Cambia</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onChange(''); }} className="text-sm text-red-600 flex items-center gap-1"><Trash2 size={14}/> Rimuovi</button>
            </div>
          </div>
        ) : uploading ? (
          <div className="text-sm text-gray-500 py-8"><Upload className="inline animate-pulse mr-2" size={16}/> Caricamento...</div>
        ) : (
          <div className="text-sm text-gray-500 py-4">
            <Upload className="inline mr-2 text-gray-400" size={22}/>
            <div className="mt-2"><strong className="text-gray-700">Clicca o trascina qui</strong> un'immagine (max 10 MB)</div>
            <div className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP, AVIF</div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {value && (
        <div className="mt-2">
          <label className="block text-xs text-gray-500 mb-1">URL immagine (o incolla URL esterno)</label>
          <Input value={value} onChange={e => onChange(e.target.value)} placeholder="https://... o /api/uploads/..." />
        </div>
      )}
    </div>
  );
}

export function Table({ columns, rows, actions, draggable, onReorder }) {
  const [dragIdx, setDragIdx] = React.useState(null);

  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx || !onReorder) return;
    const next = [...rows];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    onReorder(next);
    setDragIdx(null);
  };

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {draggable && <th className="w-8 px-2 py-3"></th>}
                {columns.map(c => <th key={c.key} className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">{c.label}</th>)}
                {actions && <th className="px-4 py-3 text-right font-semibold text-gray-700">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={columns.length + (actions ? 1 : 0) + (draggable ? 1 : 0)} className="px-4 py-8 text-center text-gray-500">Nessun elemento</td></tr>
              ) : rows.map((row, i) => (
                <tr
                  key={row.id || i}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${draggable && dragIdx === i ? 'opacity-40' : ''}`}
                  draggable={!!draggable}
                  onDragStart={() => draggable && setDragIdx(i)}
                  onDragOver={e => draggable && e.preventDefault()}
                  onDrop={() => draggable && handleDrop(i)}
                  data-testid={draggable ? `dnd-row-${i}` : undefined}
                >
                  {draggable && (
                    <td className="px-2 py-3 text-gray-400 cursor-grab active:cursor-grabbing select-none" title="Trascina per riordinare">⋮⋮</td>
                  )}
                  {columns.map(c => <td key={c.key} className="px-4 py-3 text-gray-700">{c.render ? c.render(row, i) : (row[c.key] || '—')}</td>)}
                  {actions && <td className="px-4 py-3 text-right whitespace-nowrap">{actions(row)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-8 text-center text-gray-500 text-sm">Nessun elemento</div>
        ) : rows.map((row, i) => {
          const primary = columns[0];
          const rest = columns.slice(1);
          return (
            <div key={row.id || i} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0 font-semibold text-gray-900 text-base leading-tight">
                  {primary.render ? primary.render(row, i) : (row[primary.key] || '—')}
                </div>
                {actions && <div className="flex-shrink-0">{actions(row)}</div>}
              </div>
              {rest.length > 0 && (
                <dl className="space-y-1.5 text-sm border-t border-gray-100 pt-2">
                  {rest.map(c => {
                    const val = c.render ? c.render(row, i) : (row[c.key] || '—');
                    return (
                      <div key={c.key} className="flex items-start justify-between gap-3">
                        <dt className="text-xs uppercase tracking-wide text-gray-500 font-medium flex-shrink-0">{c.label}</dt>
                        <dd className="text-gray-700 text-right min-w-0 break-words">{val}</dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 leading-tight" style={{ color: '#0f172a' }}>{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-600 mt-1" style={{ color: '#475569' }}>{subtitle}</p>}
      </div>
      {action && <div className="w-full sm:w-auto flex-shrink-0 [&>button]:w-full sm:[&>button]:w-auto [&>button]:justify-center">{action}</div>}
    </div>
  );
}
